import { defineStore } from 'pinia'
import axios from 'axios'

// Define the API base URL
const API_URL = process.env.VUE_APP_API_URL || 'http://127.0.0.1:8000'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: JSON.parse(localStorage.getItem('user')) || null,
    token: localStorage.getItem('token') || null,
    userProfile: JSON.parse(localStorage.getItem('userProfile')) || null,
    loading: false,
    error: null,
    // Masquerade state
    isMasquerading: JSON.parse(localStorage.getItem('isMasquerading')) || false,
    originalAdminUser: JSON.parse(localStorage.getItem('originalAdminUser')) || null,
    masqueradeUser: JSON.parse(localStorage.getItem('masqueradeUser')) || null
  }),
  
  getters: {
    isAuthenticated: (state) => !!state.token,
    currentUser: (state) => state.user,
    profile: (state) => state.userProfile,
    isInMasqueradeMode: (state) => state.isMasquerading,
    currentDisplayUser: (state) => state.isMasquerading ? state.masqueradeUser : state.user,
    adminUser: (state) => state.originalAdminUser
  },
  
  actions: {
    async login(email, password) {
      this.loading = true
      this.error = null
      
      try {
        const response = await axios.post(`${API_URL}/api/login`, {
          email,
          password
        })
        
        // Check if 2FA is required
        if (response.data.two_factor_required) {
          console.log('2FA verification required for this account')
          // Return a special response indicating 2FA is needed
          return {
            requiresTwoFactor: true,
            email: email,
            message: response.data.message || 'Please enter your authentication code'
          }
        }
        
        // Regular login flow - extract user and token data
        const { user, access_token, token_type } = response.data
        
        // Check for token in different formats
        const token = access_token || response.data.token || null
        
        // Make sure we have a valid token before setting it
        if (token && typeof token === 'string' && token.length > 0) {
          this.user = user
          this.token = token
          
          // Store in localStorage for persistence
          localStorage.setItem('user', JSON.stringify(user))
          localStorage.setItem('token', token)
          
          // Set the token in axios default headers 
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
          
          // Fetch the complete user profile after successful login
          await this.fetchUserProfile()
          
          return true
        } else {
          this.error = 'Invalid authentication token received'
          return false
        }
      } catch (error) {
        console.error('Login error:', error);
        this.error = error.response?.data?.message || 'Login failed'
        return false
      } finally {
        this.loading = false
      }
    },
    
    async verifyTwoFactor(email, otp) {
      this.loading = true
      this.error = null
      
      try {
        console.log('Verifying 2FA code...');
        const response = await axios.post(`${API_URL}/api/2fa/verify`, {
          email,
          otp
        });
        
        console.log('2FA verification response:', response.data);
        
        // Extract user and token data
        const { user, access_token, token_type } = response.data
        
        // Check for token in different formats
        const token = access_token || response.data.token || null
        
        // Make sure we have a valid token before setting it
        if (token && typeof token === 'string' && token.length > 0) {
          this.user = user
          this.token = token
          
          // Store in localStorage for persistence
          localStorage.setItem('user', JSON.stringify(user))
          localStorage.setItem('token', token)
          
          // Also set the token in axios default headers 
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
          
          // Fetch the complete user profile after successful 2FA verification
          await this.fetchUserProfile()
          
          return true
        } else {
          this.error = 'Invalid authentication token received'
          return false
        }
      } catch (error) {
        console.error('2FA verification error:', error);
        if (error.response?.data?.errors?.otp) {
          this.error = error.response.data.errors.otp[0]
        } else {
          this.error = error.response?.data?.message || 'Verification code is invalid'
        }
        return false
      } finally {
        this.loading = false
      }
    },
    
    async register(userData) {
      this.loading = true
      this.error = null
      
      try {
        // Add password_confirmation field if it doesn't exist
        if (userData.password && !userData.password_confirmation) {
          userData.password_confirmation = userData.password
        }
        
        // Convert firstname/lastname to firstName/lastName (camelCase) as required by API
        if (userData.firstname && userData.lastname) {
          userData.firstName = userData.firstname;
          userData.lastName = userData.lastname;
          // Also create name field as fallback
          userData.name = `${userData.firstname} ${userData.lastname}`
          
          // Remove the lowercase versions
          delete userData.firstname;
          delete userData.lastname;
        }
        
        // We'll now send the emailPreferences object as is without transforming
        // Backend API expects the nested structure
        
        console.log('Sending registration data:', userData)
        
        const response = await axios.post(`${API_URL}/api/register`, userData)
        
        // Extract user and token data
        const { user, access_token, token_type } = response.data
        
        // Check for token in different formats
        const token = access_token || response.data.token || null
        
        // Make sure we have a valid token
        if (token && typeof token === 'string' && token.length > 0) {
          this.user = user
          this.token = token
          
          // Store in localStorage for persistence
          localStorage.setItem('user', JSON.stringify(user))
          localStorage.setItem('token', token)
          
          // Also set the token in axios default headers
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
          
          return true
        } else {
          this.error = 'Invalid authentication token received'
          return false
        }
      } catch (error) {
        if (error.response?.data?.errors) {
          // Handle validation errors
          const errorMessages = Object.entries(error.response.data.errors)
            .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
            .join('\n')
          this.error = errorMessages
        } else {
          this.error = error.response?.data?.message || 'Registration failed'
        }
        return false
      } finally {
        this.loading = false
      }
    },
    
    logout() {
      // Clear user state
      this.user = null;
      this.token = null;
      this.userProfile = null;
      this.error = null;
      
      // Clear masquerade state
      this.isMasquerading = false;
      this.originalAdminUser = null;
      this.masqueradeUser = null;
      
      // Remove from localStorage
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('userProfile');
      localStorage.removeItem('isMasquerading');
      localStorage.removeItem('originalAdminUser');
      localStorage.removeItem('masqueradeUser');
      
      // Remove token from axios headers
      delete axios.defaults.headers.common['Authorization'];
    },
    
    async updateProfile(userData) {
      this.loading = true
      this.error = null
      
      try {
        // Send data to the updated API endpoint
        const response = await axios.put(
          `${API_URL}/api/user`,
          userData,
          {
            headers: { Authorization: `Bearer ${this.token}` }
          }
        )
        
        const updatedUser = response.data
        
        // Update the basic user object
        this.user = {
          ...this.user,
          ...updatedUser
        }
        localStorage.setItem('user', JSON.stringify(this.user))
        
        // Update the full profile by fetching fresh data
        await this.fetchUserProfile()
        
        return true
      } catch (error) {
        if (error.response?.data?.errors) {
          // Handle validation errors
          const errorMessages = Object.entries(error.response.data.errors)
            .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
            .join('\n')
          this.error = errorMessages
        } else {
          this.error = error.response?.data?.message || 'Profile update failed'
        }
        return false
      } finally {
        this.loading = false
      }
    },
    
    async updateNotificationPreferences(emailPreferences) {
      this.loading = true
      this.error = null
      
      try {
        // Convert boolean values to 1/0 for API and use correct field names
        const payload = {
          email_notification: emailPreferences.emailNotifications ? 1 : 0,
          content_update: emailPreferences.contentUpdates ? 1 : 0,
          billing_alerts: emailPreferences.billingAlerts ? 1 : 0,
          marketing_emails: emailPreferences.marketingEmails ? 1 : 0
        }
        
        // Send data to the API endpoint
        const response = await axios.put(
          `${API_URL}/api/user`,
          payload,
          {
            headers: { Authorization: `Bearer ${this.token}` }
          }
        )
        
        const updatedUser = response.data
        
        this.user = updatedUser
        localStorage.setItem('user', JSON.stringify(updatedUser))
        
        return true
      } catch (error) {
        if (error.response?.data?.errors) {
          // Handle validation errors
          const errorMessages = Object.entries(error.response.data.errors)
            .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
            .join('\n')
          this.error = errorMessages
        } else {
          this.error = error.response?.data?.message || 'Notification preferences update failed'
        }
        return false
      } finally {
        this.loading = false
      }
    },
    
    async validateSession() {
      console.log('Validating session, current token:', this.token ? 'exists' : 'none');
      
      if (!this.token || this.token === 'undefined' || this.token === 'null') {
        console.log('No valid token found, clearing session');
        // Clear invalid token state
        this.user = null;
        this.token = null;
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        return false;
      }
      
      try {
        console.log('Attempting to validate token with API');
        // Try to get current user data to validate token
        const response = await axios.get(`${API_URL}/api/user`, {
          headers: { 
            Authorization: `Bearer ${this.token}`,
            Accept: 'application/json'
          },
          skipAuthRedirect: true // Skip auto redirect if this fails
        });
        
        if (response.data) {
          console.log('Token validation successful, user authenticated');
          // Update user data if needed
          this.user = response.data;
          return true;
        }
        console.log('Token validation failed, no user data returned');
        return false;
      } catch (error) {
        console.error('Session validation error:', error);
        // If validation fails, clear the session
        this.user = null;
        this.token = null;
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        return false;
      }
    },
    
    // Add a new method to fetch the complete user profile
    async fetchUserProfile() {
      if (!this.token) return
      
      this.loading = true
      this.error = null
      
      try {
        const response = await axios.get(`${API_URL}/api/user/profile`, {
          headers: { Authorization: `Bearer ${this.token}` }
        })
        
        this.userProfile = response.data
        localStorage.setItem('userProfile', JSON.stringify(response.data))
        
        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || 'Failed to fetch user profile'
        return null
      } finally {
        this.loading = false
      }
    },
    
    async deleteAccount(password) {
      if (!this.token) return false
      
      this.loading = true
      this.error = null
      
      try {
        const response = await axios.delete(`${API_URL}/api/user`, {
          data: { password },
          headers: { Authorization: `Bearer ${this.token}` }
        })
        
        // Clear user data on successful deletion
        this.logout()
        
        return true
      } catch (error) {
        this.error = error.response?.data?.message || 'Failed to delete account'
        return false
      } finally {
        this.loading = false
      }
    },

    async setMasqueradeUser(accessToken, masqueradeUser, adminUser) {
      // Store original admin session
      this.originalAdminUser = this.user
      this.masqueradeUser = masqueradeUser
      this.isMasquerading = true
      
      // Set the masquerade user as current user and update token
      this.user = masqueradeUser
      this.token = accessToken
      
      // Update localStorage
      localStorage.setItem('user', JSON.stringify(masqueradeUser))
      localStorage.setItem('token', accessToken)
      localStorage.setItem('isMasquerading', JSON.stringify(true))
      localStorage.setItem('originalAdminUser', JSON.stringify(this.originalAdminUser))
      localStorage.setItem('masqueradeUser', JSON.stringify(masqueradeUser))
      
      // Set the token in axios default headers
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
      
      // Fetch the masqueraded user's profile
      await this.fetchUserProfile()
      
      console.log(`Masquerading as ${masqueradeUser.firstName} ${masqueradeUser.lastName} (${masqueradeUser.email})`)
    },

    async stopMasquerade() {
      if (!this.isMasquerading || !this.originalAdminUser) {
        console.warn('Not in masquerade mode or no original admin user found')
        return false
      }
      
      const originalAdmin = this.originalAdminUser
      
      // Clear masquerade state
      this.isMasquerading = false
      this.masqueradeUser = null
      this.originalAdminUser = null
      
      // Remove masquerade data from localStorage
      localStorage.removeItem('isMasquerading')
      localStorage.removeItem('originalAdminUser')
      localStorage.removeItem('masqueradeUser')
      
      // We need to restore the original admin session
      // Since we don't have the original admin token, we need to re-authenticate
      // For now, we'll clear the session and redirect to login
      this.logout()
      
      console.log('Stopped masquerading, returning to admin session')
      
      // Redirect to login or admin dashboard
      window.location.href = '/login'
      
      return true
    }
  }
}) 